import React, { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Paperclip, FileText } from "lucide-react";
import MobileSheet from "@/components/MobileSheet";

/**
 * Buyer payment-reference modal. Shared by:
 *  - `OrderBillingPanel.BuyerPaidButton` (the inline panel CTA)
 *  - `OrderNextAction` (the sticky next-step card, opens the same modal)
 *
 * Both entry points must produce identical behaviour: the buyer is required
 * to enter method + reference, optionally attach a proof, and the order
 * status only flips to `buyer_confirmed_paid` after the form submits
 * successfully.
 */
export default function MarkPaidModal({ orderId, open, onClose, onSuccess }) {
  const [method, setMethod] = useState("Wire");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [proofUploadId, setProofUploadId] = useState(null);
  const [proofMeta, setProofMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  // Reset on close so a re-open starts clean.
  const close = () => {
    setReference("");
    setNotes("");
    setProofUploadId(null);
    setProofMeta(null);
    setUploadErr("");
    setErr("");
    onClose?.();
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setUploadErr("File too large (max 10 MB)."); return; }
    setUploadErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("purpose", "payment_proof"); fd.append("related_id", orderId);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": undefined } });
      setProofUploadId(data.id);
      setProofMeta({ name: data.original_filename, size: data.size });
    } catch (e2) {
      setUploadErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim()) return;
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/orders/${orderId}/mark-paid`, {
        method,
        reference: reference.trim(),
        notes: notes.trim() || null,
        proof_upload_id: proofUploadId || null,
      });
      onSuccess?.();
      close();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || uploading;

  return (
    <MobileSheet open={open} onClose={close} title="PAYMENT REFERENCE" testId="mark-paid-sheet">
      <form onSubmit={submit} style={{ display: "grid", gap: 10 }}>
        <div style={{ fontSize: 12, color: "var(--ink-mute)" }}>
          Pay your supplier off-platform (wire, EFT, cheque…). Record your reference here and attach proof if you have it. Your supplier sees both and confirms receipt. The platform never moves the funds.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <div>
            <label className="label">Method *</label>
            <select className="input" data-testid="paid-method" value={method} onChange={e => setMethod(e.target.value)}>
              {["Wire", "EFT", "Cheque", "Credit", "Other"].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Payment reference / txn # *</label>
            <input className="input" data-testid="paid-reference" required minLength={2} maxLength={120}
                   placeholder="e.g. WIRE-2026-04-2231"
                   value={reference} onChange={e => setReference(e.target.value)} />
          </div>
        </div>

        <div>
          <label className="label">Notes for supplier (optional)</label>
          <textarea className="input" style={{ minHeight: 60 }} data-testid="paid-notes"
                    value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="e.g. Sent from CIBC account ending 4422." />
        </div>

        <div>
          <label className="label">Payment proof (optional) — PDF or image, max 10 MB</label>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
            onChange={onPickFile}
            disabled={uploading}
            data-testid="paid-proof-input"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          {!proofUploadId ? (
            <button type="button"
                    data-testid="paid-proof-pick"
                    className="btn btn-ghost form-trigger-btn"
                    onClick={() => inputRef.current && inputRef.current.click()}
                    disabled={uploading}
                    style={{ margin: 0 }}>
              <Paperclip size={16} /> {uploading ? "Uploading…" : "Attach payment proof"}
            </button>
          ) : (
            <div data-testid="paid-proof-attached" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 4, background: "rgba(245,158,11,.05)" }}>
              <FileText size={16} color="var(--amber-400)" />
              <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                <div style={{ color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{proofMeta?.name || "Proof attached"}</div>
                {proofMeta?.size && <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{Math.ceil(proofMeta.size / 1024)} KB</div>}
              </div>
              <button type="button" data-testid="paid-proof-clear" className="btn btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} onClick={() => { setProofUploadId(null); setProofMeta(null); }}>Replace</button>
            </div>
          )}
          {uploadErr && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 6 }} data-testid="paid-proof-error">{uploadErr}</div>}
        </div>

        {err && <div style={{ color: "var(--red)", fontSize: 13 }} data-testid="paid-submit-error">{err}</div>}

        <div className="form-action-row">
          <button data-testid="paid-submit" className="btn btn-primary" disabled={busy || !reference.trim()}>
            {submitting ? "Submitting…" : uploading ? "Uploading…" : "Submit payment reference"}
          </button>
          <button type="button" className="btn btn-ghost" onClick={close}>Cancel</button>
        </div>
      </form>
    </MobileSheet>
  );
}
